import { useState, type FormEvent } from 'react';
import { sendContact, type SendContactInput } from '../api/client';

const MESSAGE_MAX = 4000;
const FALLBACK_ERROR = '送信に失敗しました。時間をおいて再度お試しください。';

const CATEGORY_OPTIONS: { value: SendContactInput['category']; label: string }[] = [
  { value: 'general', label: '一般的なお問い合わせ' },
  { value: 'rights', label: '権利侵害の申告（送信防止措置の申出）' },
  { value: 'privacy', label: '個人情報の開示等に関する請求' },
  { value: 'bug', label: '不具合の報告' },
  { value: 'other', label: 'その他' },
];

export default function Contact() {
  const [category, setCategory] = useState<SendContactInput['category']>('general');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setSent(false);
    try {
      await sendContact({
        category,
        name: name.trim(),
        email: email.trim(),
        message,
        websiteUrl,
      });
      setCategory('general');
      setName('');
      setEmail('');
      setMessage('');
      setWebsiteUrl('');
      setSent(true);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : FALLBACK_ERROR);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <h1>お問い合わせ</h1>
      <p>
        サービスに関するご質問・ご要望・不具合報告・権利侵害の申告などを、以下のフォームからお送りください。数日以内にご返信いたします。
      </p>

      {sent && (
        <div className="auth-form__success">
          お問い合わせを受け付けました。数日以内にご返信いたします。
        </div>
      )}

      <form className="form" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="category">カテゴリー</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as SendContactInput['category'])}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="name">名前</label>
          <input id="name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="email">返信用メールアドレス *</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            maxLength={100}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="message">
            本文 * ({message.length}/{MESSAGE_MAX})
          </label>
          <textarea
            id="message"
            rows={8}
            required
            maxLength={MESSAGE_MAX}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        {/* honeypot: bot 対策。人間には見えない項目で、値が入っていたら送信を無視する（サーバ側で判定）。 */}
        <input
          name="websiteUrl"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
        />

        {error && <div className="auth-form__error">{error}</div>}

        <div>
          <button
            type="submit"
            className="btn btn--primary"
            style={{ width: '100%' }}
            disabled={busy}
          >
            {busy ? '送信中…' : '送信する'}
          </button>
        </div>
      </form>
    </div>
  );
}
